// src/lib/recebimentoFaturaTela.ts
//
// Regra da tela "Recebimento de Faturas" (projeto 12, demanda 02, #5835): o que
// precisa de atenção entre os PDFs que chegaram em assinatura@ e se a varredura
// da caixa está em dia. Módulo sem I/O: a tela busca, este arquivo decide.

export type EtapaRecebimento =
  | 'nao_capturado' | 'capturado' | 'processando' | 'falha' | 'processado' | 'duplicata' | 'descartado';

export const ROTULO_ETAPA: Record<EtapaRecebimento, string> = {
  nao_capturado: 'Não capturado',
  capturado: 'Aguardando o robô',
  processando: 'Processando',
  falha: 'Falha',
  processado: 'Processado',
  duplicata: 'Duplicata',
  descartado: 'Descartado',
};

export const ROTULO_ERRO_ETAPA: Record<string, string> = {
  extracao: 'na leitura do PDF',
  upload_pdf: 'no envio do PDF ao site',
  gravacao: 'ao gravar a fatura',
};

/** Processando há mais que isso = o robô parou no meio (execução caiu). */
export const MINUTOS_TRAVADO = 15;
/** A varredura roda de hora em hora; sem conclusão há mais que isso, está atrasada. */
export const MINUTOS_CONFERENCIA_ATRASADA = 150;

export interface Recebimento {
  id: number;
  etapa: EtapaRecebimento | string;
  recebido_em: string;
  capturado_em?: string | null;
  processamento_iniciado_em?: string | null;
  processado_em?: string | null;
  tentativas?: number | null;
  erro_etapa?: string | null;
  erro_codigo?: string | null;
  erro_mensagem?: string | null;
  uc_codigo?: string | null;
  uc_codigo_norm?: string | null;
  mes_referencia?: string | null;
  assunto?: string | null;
  anexo_nome?: string | null;
  fatura_id?: number | null;
}

export type Situacao = 'atencao' | 'andamento' | 'ok';

export interface Classificacao {
  situacao: Situacao;
  /** Frase curta para a coluna "o que houve". */
  motivo: string;
  travado: boolean;
}

const minutosEntre = (de: string | null | undefined, ate: Date): number | null => {
  if (!de) return null;
  const t = new Date(de).getTime();
  return Number.isFinite(t) ? (ate.getTime() - t) / 60000 : null;
};

export function classificar(r: Recebimento, agora: Date): Classificacao {
  const erro = [r.erro_etapa ? `Falhou ${ROTULO_ERRO_ETAPA[r.erro_etapa] ?? r.erro_etapa}` : null, r.erro_codigo]
    .filter(Boolean).join(' · ');
  switch (r.etapa) {
    case 'falha':
      return { situacao: 'atencao', motivo: erro || 'Falhou sem detalhe', travado: false };
    case 'nao_capturado':
      return { situacao: 'atencao', motivo: 'Chegou na caixa e o robô não pegou', travado: false };
    case 'processando': {
      const min = minutosEntre(r.processamento_iniciado_em, agora);
      const travado = min !== null && min > MINUTOS_TRAVADO;
      return travado
        ? { situacao: 'atencao', motivo: `Parado em "processando" há ${formatarDuracao(min!)}`, travado: true }
        : { situacao: 'andamento', motivo: 'O robô está processando', travado: false };
    }
    case 'capturado': {
      const min = minutosEntre(r.capturado_em ?? r.recebido_em, agora);
      const travado = min !== null && min > MINUTOS_TRAVADO;
      return travado
        ? { situacao: 'atencao', motivo: `Registrado e não processado há ${formatarDuracao(min!)}`, travado: true }
        : { situacao: 'andamento', motivo: 'Aguardando o robô', travado: false };
    }
    case 'duplicata':
      return { situacao: 'ok', motivo: r.fatura_id ? 'Já existia fatura deste mês' : 'Duplicata, sem a fatura original localizada', travado: false };
    case 'processado':
      return { situacao: 'ok', motivo: 'Fatura gravada', travado: false };
    case 'descartado':
      return { situacao: 'ok', motivo: 'Descartado', travado: false };
    default:
      return { situacao: 'atencao', motivo: `Etapa desconhecida: ${r.etapa}`, travado: false };
  }
}

export function formatarDuracao(minutos: number): string {
  const m = Math.max(0, Math.round(minutos));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  return `${Math.floor(h / 24)} dias`;
}

export interface Resumo {
  total: number;
  atencao: number;
  falha: number;
  naoCapturado: number;
  travado: number;
  andamento: number;
  processado: number;
  duplicata: number;
  /** Mediana de recebido → processado, em minutos (só os processados). */
  medianaMinutos: number | null;
}

export function resumir(linhas: Recebimento[], agora: Date): Resumo {
  const r: Resumo = { total: linhas.length, atencao: 0, falha: 0, naoCapturado: 0, travado: 0, andamento: 0, processado: 0, duplicata: 0, medianaMinutos: null };
  const tempos: number[] = [];
  for (const l of linhas) {
    const c = classificar(l, agora);
    if (c.situacao === 'atencao') r.atencao++;
    if (c.situacao === 'andamento') r.andamento++;
    if (c.travado) r.travado++;
    if (l.etapa === 'falha') r.falha++;
    if (l.etapa === 'nao_capturado') r.naoCapturado++;
    if (l.etapa === 'duplicata') r.duplicata++;
    if (l.etapa === 'processado') {
      r.processado++;
      const t = l.processado_em ? minutosEntre(l.recebido_em, new Date(l.processado_em)) : null;
      if (t !== null && t >= 0) tempos.push(t);
    }
  }
  if (tempos.length) {
    tempos.sort((a, b) => a - b);
    const meio = Math.floor(tempos.length / 2);
    r.medianaMinutos = tempos.length % 2 ? tempos[meio] : (tempos[meio - 1] + tempos[meio]) / 2;
  }
  return r;
}

export interface Conferencia {
  id: number;
  iniciada_em: string;
  concluida_em: string | null;
  janela_inicio?: string | null;
  emails_encontrados: number | null;
  anexos_encontrados: number | null;
  nao_capturados: number | null;
  erro: string | null;
}

export type EstadoConferencia = 'nunca' | 'ok' | 'rodando' | 'incompleta' | 'atrasada';

/**
 * Estado da varredura a partir das conferências mais recentes (qualquer ordem).
 * "incompleta" = a última começou e não terminou (erro ou execução caiu);
 * "atrasada" = a última concluída é velha demais (a varredura parou de rodar).
 */
export function estadoConferencia(conferencias: Conferencia[], agora: Date): {
  estado: EstadoConferencia; ultima: Conferencia | null; ultimaConcluida: Conferencia | null;
} {
  const ordenadas = [...conferencias].sort((a, b) => b.iniciada_em.localeCompare(a.iniciada_em));
  const ultima = ordenadas[0] ?? null;
  const ultimaConcluida = ordenadas.find((c) => c.concluida_em) ?? null;
  if (!ultima) return { estado: 'nunca', ultima, ultimaConcluida };
  if (!ultima.concluida_em) {
    const min = minutosEntre(ultima.iniciada_em, agora) ?? 0;
    if (!ultima.erro && min <= MINUTOS_TRAVADO) return { estado: 'rodando', ultima, ultimaConcluida };
    return { estado: 'incompleta', ultima, ultimaConcluida };
  }
  const min = minutosEntre(ultima.concluida_em, agora) ?? 0;
  return { estado: min > MINUTOS_CONFERENCIA_ATRASADA ? 'atrasada' : 'ok', ultima, ultimaConcluida };
}

/** Busca por UC (ignora pontuação e zeros à esquerda), assunto ou nome do arquivo. */
export function casaBusca(r: Recebimento, termo: string): boolean {
  const t = termo.trim().toLowerCase();
  if (!t) return true;
  const num = t.replace(/\D/g, '').replace(/^0+/, '');
  const uc = (r.uc_codigo_norm ?? r.uc_codigo ?? '').replace(/\D/g, '').replace(/^0+/, '');
  if (num && uc.includes(num)) return true;
  return [r.assunto, r.anexo_nome, r.erro_mensagem, r.erro_codigo].some((s) => (s ?? '').toLowerCase().includes(t));
}
